import React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app";
import { DataLayer, DataLayerContext } from "./dataLayer/DataLayer";
import { rehydrateAuth, setTestSignedIn } from "./auth/authState";
import { googleAuth } from "./auth/googleAuth";
import { appleAuth } from "./auth/appleAuth";
import { isTestMode } from "./testMode";
import { preloadLogo } from "./screens/components/preloadLogo";
import { hideNativeSplash } from "./screens/components/nativeSplash";
import { registerNativeDeepLinkHandler, registerNativeAppResumeHandler } from "./nativeDeepLink";
import { installDiagnosticCapture } from "./diagnostics";
import { registerStorePurchaseUpdates } from "./storeBilling";

installDiagnosticCapture();

// Start decoding the mascot immediately, in parallel with the auth bootstrap,
// so it's ready to paint by the time the connecting screen flies it in. This is
// what keeps the launch animation from showing a blank frame or popping the
// logo in halfway through the fly-in.
preloadLogo();

// Safety net: with `launchAutoHide: false` on native, the splash only goes away
// when the web app calls hide(). The first screen (ConnectingScreen /
// SignInScreen) does that, but if anything upstream throws before a screen
// mounts, the splash would stick forever. Force-hide after a generous timeout so
// a broken boot never traps the user on the splash. No-op on web.
setTimeout(() => hideNativeSplash(), 4000);

const testMode = isTestMode();
if (!testMode) rehydrateAuth();

const dataLayer = new DataLayer();
dataLayer.capturePremiumCallbackNotice();

// Capture a friend-invite deep link (/?invite={code}) before anything else so
// the code survives the auth bootstrap. The link hangs off the root document so
// it's served by the same CDN-cached index.html as the app (no SPA rewrite or
// Static Web Apps navigationFallback needed). We stash it on the DataLayer
// (which mirrors it to sessionStorage) and strip the query so a refresh or
// sign-in redirect doesn't lose or re-trigger it. The Friends screen prompts the
// user to accept after sign-in.
(() => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("invite");
  if (code && /^[A-Za-z0-9_-]{16,64}$/.test(code)) {
    dataLayer.setPendingInviteCode(code);
    params.delete("invite");
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }
})();

// On native iOS the invite arrives as a Universal Link through Capacitor's App
// plugin rather than in window.location (the shell loads the remote PWA), so wire
// that to the same pending-invite flow. No-op on web / PWA.
registerNativeDeepLinkHandler(dataLayer);
// The installed app can leave its WebView alive while Patreon opens in the
// system browser. Capacitor's active-state event feeds the same deduped
// entitlement refresh as web focus/visibility/pageshow.
registerNativeAppResumeHandler(dataLayer);
void registerStorePurchaseUpdates(() => dataLayer.handleAppResume());

// Initialize auth state from localStorage and kick off SDK loads BEFORE
// React mounts, so the first render sees the right auth state.
if (testMode) {
  // Local test mode: fake sign-in + a hardcoded puzzle, no provider SDKs.
  setTestSignedIn();
  dataLayer.enterTestMode();
  (window as unknown as { __wirderTestDataLayer?: DataLayer }).__wirderTestDataLayer = dataLayer;
} else {
  googleAuth.init();
  appleAuth.init();
}

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(
  <DataLayerContext.Provider value={dataLayer}>
    <App />
  </DataLayerContext.Provider>,
);
